'use client';
'use strict';

var _tslib = require('../../../_virtual/_tslib.js');
var jsxRuntime = require('react/jsx-runtime');
var tailwindMerge = require('tailwind-merge');
var react = require('react');
require('../../DataDisplays/Avatar/Avatar.js');
require('../../Base/Layouts/Box/Box.js');
require('../../Base/Layouts/FullBleed/FullBleed.js');
require('../../DataDisplays/Label/Label.js');
var SystemIcon = require('../../DataDisplays/SystemIcon/SystemIcon.js');
var Divider$1 = require('../../DataDisplays/Divider/Divider.js');
var AutoCompleteList_parts = require('./AutoCompleteList.parts.js');

/**
 * 자동완성 목록의 Root 컴포넌트입니다.
 * 자동완성 항목들을 감싸는 컨테이너 역할을 합니다.
 *
 * @param {Object} props
 * @param {ReactNode} props.children - 자동완성 목록에 표시될 자식 컴포넌트들 (AutoCompleteList.Item, AutoCompleteList.Divider)
 *
 * @example
 * ```tsx
 * <AutoCompleteList.Root>
 *   <AutoCompleteList.Item
 *     text="검색 항목"
 *     leadingIcon="search"
 *     count={5}
 *   />
 *   <AutoCompleteList.Divider />
 *   <AutoCompleteList.Item
 *     text="다른 항목"
 *     iconUrl="/icons/custom.png"
 *   />
 * </AutoCompleteList.Root>
 * ```
 */ const Root = (_a)=>{
    var { children, className, hasIndexBar = false, onIndexClick } = _a, rest = _tslib.__rest(_a, [
        "children",
        "className",
        "hasIndexBar",
        "onIndexClick"
    ]);
    const ref = react.useRef(null);
    const [isScrollable, setIsScrollable] = react.useState(false);
    react.useLayoutEffect(()=>{
        const autoCompleteList = ref.current;
        if (!autoCompleteList) return;
        const { scrollHeight, clientHeight } = autoCompleteList;
        setIsScrollable(scrollHeight > clientHeight);
    }, [
        children
    ]);
    return jsxRuntime.jsxs("div", Object.assign({
        className: tailwindMerge.twMerge('flex max-h-[436px] w-full rounded-xl shadow-graymedium', className)
    }, rest, {
        children: [
            hasIndexBar && jsxRuntime.jsx(AutoCompleteList_parts.IndexBar, {
                onIndexClick: onIndexClick
            }),
            jsxRuntime.jsx("ul", {
                ref: ref,
                "data-testid": "design-system-autoCompleteList",
                className: tailwindMerge.twMerge('flex w-full flex-col bg-w-white py-2 pr-2', 'overflow-y-auto scrollbar-list', hasIndexBar ? 'rounded-r-xl' : 'rounded-xl', isScrollable && 'pr-0'),
                children: children
            })
        ]
    }));
};
/**
 * 자동완성 목록의 개별 항목 컴포넌트입니다.
 * @param {Object} props
 * @param {string} props.text - 항목에 표시될 텍스트
 * @param {number} [props.count] - 항목의 개수 (있는 경우 우측에 표시)
 * @param {SystemIconName} [props.leadingIcon] - 항목 앞에 표시될 시스템 아이콘
 * @param {string} [props.iconUrl] - 항목 앞에 표시될 커스텀 아이콘 URL
 * @param {boolean} [props.isInputItem=false] - 입력 항목 여부
 * @param {boolean} [props.isFocused=false] - 포커스 상태 여부
 * @param {string} [props.inputValue] - 입력값 (isInputItem이 true인 경우 사용)
 * @param {string} [props.className] - 추가적인 CSS 클래스
 */ const Item = (_a)=>{
    var { text, count, leadingIcon, iconUrl, isInputItem = false, isFocused = false, inputValue, className } = _a, rest = _tslib.__rest(_a, [
        "text",
        "count",
        "leadingIcon",
        "iconUrl",
        "isInputItem",
        "isFocused",
        "inputValue",
        "className"
    ]);
    return jsxRuntime.jsxs("li", Object.assign({
        className: tailwindMerge.twMerge('visible ml-2 flex cursor-pointer select-none items-center gap-2 rounded-xl bg-w-white px-4 py-3 hover:bg-primary-10', isFocused && 'bg-primary-10', className)
    }, rest, {
        "data-testid": "design-system-list-item",
        children: [
            leadingIcon && jsxRuntime.jsx(SystemIcon, {
                name: leadingIcon,
                className: "text-w-gray-600",
                testId: "design-system-list-item-leading-icon"
            }),
            iconUrl && jsxRuntime.jsx(AutoCompleteList_parts.Icon, {
                iconUrl: iconUrl
            }),
            jsxRuntime.jsx(AutoCompleteList_parts.TextSection, {
                text: text,
                isInputItem: isInputItem,
                inputValue: inputValue
            }),
            jsxRuntime.jsx(AutoCompleteList_parts.CountSection, {
                count: count
            })
        ]
    }));
};
/**
 * 자동완성 목록의 구분선 컴포넌트입니다.
 */ const Divider = ()=>{
    return jsxRuntime.jsx(Divider$1, {
        className: "my-2"
    });
};
/**
 * 자동완성 목록을 표시하는 컴포넌트입니다.
 *
 * @module AutoCompleteList
 * @property {Component} Root - 자동완성 목록의 컨테이너 컴포넌트입니다. 스크롤 가능한 목록과 인덱스 바를 포함합니다.
 * @property {Component} Item - 자동완성 목록의 개별 항목을 표시하는 컴포넌트입니다. 텍스트, 아이콘, 카운트 등을 표시할 수 있습니다.
 * @property {Component} Divider - 자동완성 목록의 항목들을 구분하는 구분선 컴포넌트입니다.
 * @example
 * ```tsx
 * <AutoCompleteList.Root>
 *   <AutoCompleteList.Item
 *     text="검색 항목"
 *     leadingIcon="search"
 *     count={5}
 *   />
 *   <AutoCompleteList.Divider />
 *   <AutoCompleteList.Item
 *     text="다른 항목"
 *     iconUrl="/icons/custom.png"
 *   />
 * </AutoCompleteList.Root>
 * ```
 */ const AutoCompleteList = {
    Root,
    Item,
    Divider
};

module.exports = AutoCompleteList;
